// packages/shared/src/roleforge/seat.ts
//
// Seat schema (RoleForge v2 spec, docs/superpowers/specs/2026-08-09-
// roleforge-v2-pairing-blueprint-design.md). A seat is a human+AI pairing —
// the role model this program replaces the pain-audit engine with. `level`
// is intentionally two-valued (member | manager): RoleForge orgs are flat by
// design, no deeper ladder. `department.compartment` flags a signal-driven
// need-to-know compartment (not every department is one). Starter department
// keys (sales|marketing|finance|operations|customer_service) live at
// apps/server/src/lib/knowledge-provenance-map.ts; `department.key` also
// accepts blueprint-invented slugs beyond that starter set.
// `customizations` is a legacy pass-through field for pre-schema data — new
// fields belong on the schema itself, not in that bag. `.strict()` throughout
// (funnel-blueprint.ts's pattern): LLM-emitted payloads must surface unknown
// keys, never silently drop them.

import { z } from 'zod';

export const SEAT_LEVELS = ['member', 'manager'] as const;
export type SeatLevel = (typeof SEAT_LEVELS)[number];

export const SeatDepartmentSchema = z
  .object({
    key: z.string().min(1).optional(),   // starter key: sales|marketing|finance|operations|customer_service or blueprint-invented slug
    name: z.string().min(1).optional(),  // display name for non-starter departments (incl. compartments)
    compartment: z.boolean().optional(), // signal-driven need-to-know compartment
  })
  .strict()
  .refine((d) => Boolean(d.key || d.name), { message: 'department needs key or name' });

export const SeatToolSchema = z
  .object({
    template_id: z.string().min(1).optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    instructions: z.string().optional(),
  })
  .strict();

export const SeatObjectSchema = z
  .object({
    id: z.string().uuid().optional(),
    position: z.number().int().min(0).optional(),
    name: z.string().min(1),
    emoji: z.string().optional(),
    department: SeatDepartmentSchema,
    level: z.enum(SEAT_LEVELS),
    human: z
      .object({
        summary: z.string().min(1),
        person: z
          .object({ name: z.string().min(1), email: z.string().email().optional() })
          .strict()
          .optional(),
      })
      .strict(),
    ai: z
      .object({
        charter: z.string().min(1),
        decision_rights: z.array(z.string()).optional(),
      })
      .strict(),
    role_template_id: z.string().min(1).optional(),
    bespoke_role: z
      .object({
        name: z.string().min(1),
        description: z.string(),
        instructions: z.string(),
        responsibilities: z.array(z.string()),
      })
      .strict()
      .optional(),
    tools: z.array(SeatToolSchema).optional(),
    customizations: z.record(z.unknown()).optional(), // legacy pass-through; new fields are first-class
    public_persona: z
      .object({
        name: z.string().min(1),
        voice_hint: z.string().optional(),
        channels: z.array(z.string()).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export const SeatSchema = SeatObjectSchema.refine(
  (s) => Boolean(s.role_template_id) !== Boolean(s.bespoke_role),
  { message: 'exactly one of role_template_id or bespoke_role must be set' },
);

export type Seat = z.infer<typeof SeatSchema>;

export function validateSeat(input: unknown):
  | { ok: true; seat: Seat }
  | { ok: false; errors: string[] } {
  const r = SeatSchema.safeParse(input);
  return r.success
    ? { ok: true, seat: r.data }
    : {
        ok: false,
        // The top-level XOR refine (role_template_id vs bespoke_role) has no
        // path, so `path.join('.')` is '' — guard it so those errors read as
        // plain messages instead of a stray leading ": ".
        errors: r.error.issues.map((i) =>
          i.path.length ? `${i.path.join('.')}: ${i.message}` : i.message,
        ),
      };
}
